import { i18n } from 'next-i18next';

type Bundle = { default: Record<string, unknown> };
type Loaders = Record<'it' | 'en', () => Promise<Bundle>>;

// Namespaces only the steps after the venue search need. /getting-started ships just the
// step 1 namespaces in getStaticProps, so these ~50 KB stay out of the first HTML; they are
// bundled as separate chunks and added to the i18n store before a later step renders.
//
// This is a narrow exception to the app rule "don't fetch translations client-side for SSR
// pages": the strings come from the same JSON files, through the build, not from a separate
// i18n backend. A namespace added here must also be removed from getStaticProps, and vice versa.
const LATER_STEP_NAMESPACES: Record<string, Loaders> = {
  'job-positions-page': {
    it: () => import('../../public/locales/it/job-positions-page.json'),
    en: () => import('../../public/locales/en/job-positions-page.json'),
  },
  'job-position-page': {
    it: () => import('../../public/locales/it/job-position-page.json'),
    en: () => import('../../public/locales/en/job-position-page.json'),
  },
  'payment-and-invoices': {
    it: () => import('../../public/locales/it/payment-and-invoices.json'),
    en: () => import('../../public/locales/en/payment-and-invoices.json'),
  },
};

const FALLBACK_LANGUAGE = 'it';

let pending: Promise<void> | null = null;

/**
 * Adds the later-step namespaces to the i18n store, for the current language and for the
 * Italian fallback (what serverSideTranslations would have shipped). Safe to call many times:
 * the work runs once per tab, and bundles already in the store are skipped.
 */
export const loadLaterStepTranslations = (): Promise<void> => {
  if (pending) return pending;
  const instance = i18n;
  if (!instance) return Promise.resolve();

  const languages = Array.from(new Set([instance.language, FALLBACK_LANGUAGE])).filter(
    (lng): lng is 'it' | 'en' => lng === 'it' || lng === 'en'
  );
  pending = Promise.all(
    Object.entries(LATER_STEP_NAMESPACES).flatMap(([ns, loaders]) =>
      languages
        .filter((lng) => !instance.hasResourceBundle(lng, ns))
        .map((lng) => loaders[lng]().then((bundle) => instance.addResourceBundle(lng, ns, bundle.default, true, false)))
    )
  )
    .then(() => undefined)
    .catch(() => {
      // A failed chunk leaves raw keys on screen; let a later call try again.
      pending = null;
    });
  return pending;
};

/** Wraps a lazy step import so the step renders only once its translations are in the store. */
export const withLaterStepTranslations = <T>(load: () => Promise<T>): (() => Promise<T>) => {
  return () => Promise.all([load(), loadLaterStepTranslations()]).then(([module]) => module);
};
